import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import './Admin.css';

const emptyForm = {
  employeeName: '', phone: '', residentIdMasked: '', birthDate: '', address: '',
  bankName: '', bankAccount: '', emergencyContact: '', notes: '',
};

export default function AdminFreelancers() {
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'detail' | 'form'
  const [selected, setSelected] = useState(null); // 상세 조회 데이터
  const [editing, setEditing] = useState(null); // 폼 초기값

  useEffect(() => { if (view === 'list') loadAll(); }, [view]);

  async function loadAll() {
    try {
      setLoading(true);
      const data = await adminApi.get('/freelancers');
      setFreelancers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id) {
    try {
      const data = await adminApi.get(`/freelancers/${id}`);
      setSelected(data);
      setView('detail');
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleDelete(f) {
    if (!window.confirm(`"${f.employeeName}"님을 프리랜서 목록에서 삭제하시겠습니까?`)) return;
    try {
      await adminApi.delete(`/freelancers/${f.id}`);
      loadAll();
    } catch (err) {
      alert(err.message);
    }
  }

  if (view === 'form') {
    return (
      <AdminLayout>
        <FreelancerForm
          initial={editing}
          onCancel={() => { setView(selected ? 'detail' : 'list'); }}
          onSaved={async (id) => {
            if (id) await loadDetail(id);
            else if (selected) await loadDetail(selected.id);
            else setView('list');
          }}
        />
      </AdminLayout>
    );
  }

  if (view === 'detail' && selected) {
    return (
      <AdminLayout>
        <FreelancerDetail
          data={selected}
          onBack={() => { setSelected(null); setView('list'); }}
          onEdit={() => { setEditing(selected); setView('form'); }}
          onRefresh={() => loadDetail(selected.id)}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>프리랜서 관리</h1>
          <p className="text-muted">고정적으로 관리하는 인력의 인사 정보 및 업무 이력을 관리합니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing({}); setView('form'); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          신규 등록
        </button>
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}
      {!loading && !error && (
        freelancers.length === 0 ? (
          <div className="state-message">등록된 프리랜서가 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>개인ID</th>
                  <th>이름</th>
                  <th>휴대폰</th>
                  <th>등록일</th>
                  <th style={{ width: 180 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {freelancers.map(f => (
                  <tr key={f.id}>
                    <td><span className="badge badge-navy" style={{ fontFamily: 'monospace' }}>{f.employeeId}</span></td>
                    <td className="admin-table__title">{f.employeeName}</td>
                    <td>{f.phone}</td>
                    <td>{String(f.createdAt).slice(0, 10)}</td>
                    <td className="admin-table__actions">
                      <button className="admin-action-btn" onClick={() => loadDetail(f.id)}>상세보기</button>
                      <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(f)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}

/* ── 상세보기 ── */
function FreelancerDetail({ data, onBack, onEdit, onRefresh }) {
  const navigate = useNavigate();

  const infoItems = [
    { label: '개인ID', value: data.employeeId },
    { label: '이름', value: data.employeeName },
    { label: '주민번호', value: data.residentIdMasked || '-' },
    { label: '휴대폰번호', value: data.phone },
    { label: '비상연락처', value: data.emergencyContact || '-' },
    { label: '생년월일', value: data.birthDate || '-' },
    { label: '주소', value: data.address || '-' },
    { label: '은행명', value: data.bankName || '-' },
    { label: '계좌번호', value: data.bankAccount || '-' },
  ];

  function goToNewContract() {
    // 근로계약서 등록 화면으로 이동 (freelancerId를 쿼리스트링으로 전달)
    navigate(`/admin/contracts?freelancerId=${data.id}`);
  }

  return (
    <div>
      <button className="back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        목록으로
      </button>
      <div className="admin-form-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>{data.employeeName} 님</h2>
          <button className="btn btn-outline-navy" onClick={onEdit}>정보 수정</button>
        </div>

        <h4 className="admin-form-section-title">인적 정보</h4>
        <div className="admin-detail-grid">
          {infoItems.map(item => (
            <div key={item.label} className="admin-detail-item">
              <span className="admin-detail-item__label">{item.label}</span>
              <span className="admin-detail-item__value">{item.value}</span>
            </div>
          ))}
        </div>

        {data.notes && (
          <>
            <h4 className="admin-form-section-title">비고</h4>
            <p style={{ color: 'var(--gray-700)', whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{data.notes}</p>
          </>
        )}

        <h4 className="admin-form-section-title" style={{ marginTop: '2rem' }}>
          수행 업무 이력
          <button
            className="btn btn-primary"
            style={{ marginLeft: '1rem', padding: '0.3rem 0.85rem', fontSize: '0.82rem' }}
            onClick={goToNewContract}
          >
            + 추가
          </button>
        </h4>

        {!data.contracts || data.contracts.length === 0 ? (
          <div className="state-message" style={{ padding: '2rem 0', textAlign: 'left', color: 'var(--gray-400)', fontSize: '0.9rem' }}>
            등록된 업무 이력이 없습니다.
          </div>
        ) : (
          <div className="admin-table-wrapper" style={{ marginTop: '0.5rem' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>서명</th>
                  <th>업무명</th>
                  <th>근무지</th>
                  <th>근로기간</th>
                  <th>일급</th>
                  <th>등록일</th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map(c => (
                  <tr key={c.id}>
                    <td>
                      <span className={`badge ${c.is_signed ? 'badge-green' : 'badge-accent'}`}>
                        {c.is_signed ? '서명완료' : '서명대기'}
                      </span>
                    </td>
                    <td>{c.event_name || '-'}</td>
                    <td>{c.work_place || '-'}</td>
                    <td>{c.work_period_from} ~ {c.work_period_to}</td>
                    <td>{c.daily_wage ? Number(c.daily_wage).toLocaleString() + '원' : '-'}</td>
                    <td>{String(c.created_at).slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 등록/수정 폼 ── */
function FreelancerForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    employeeName: initial?.employeeName || '',
    phone: initial?.phone || '',
    residentIdMasked: initial?.residentIdMasked || '',
    birthDate: initial?.birthDate || '',
    address: initial?.address || '',
    bankName: initial?.bankName || '',
    bankAccount: initial?.bankAccount || '',
    emergencyContact: initial?.emergencyContact || '',
    notes: initial?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employeeName || !form.phone) {
      setError('이름과 휴대폰번호는 필수입니다.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await adminApi.put(`/freelancers/${initial.id}`, form);
        await onSaved(null);
      } else {
        const result = await adminApi.post('/freelancers', form);
        await onSaved(result.id);
      }
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        {isEdit ? '상세보기로' : '목록으로'}
      </button>
      <div className="admin-form-card">
        <h2>{isEdit ? '프리랜서 정보 수정' : '신규 프리랜서 등록'}</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}
          <div className="form-row-2">
            <div className="form-group">
              <label>이름 <span className="required">*</span></label>
              <input name="employeeName" value={form.employeeName} onChange={handleChange} placeholder="홍길동" />
            </div>
            <div className="form-group">
              <label>주민번호</label>
              <input name="residentIdMasked" value={form.residentIdMasked} onChange={handleChange} placeholder="990101-1234567" inputMode="numeric" />
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label>휴대폰번호 <span className="required">*</span></label>
              <input name="phone" value={form.phone} onChange={handleChange} placeholder="01012345678" inputMode="tel" />
            </div>
            <div className="form-group">
              <label>비상연락처</label>
              <input name="emergencyContact" value={form.emergencyContact} onChange={handleChange} placeholder="비상시 연락처" inputMode="tel" />
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label>생년월일</label>
              <input type="date" name="birthDate" value={form.birthDate} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>주소</label>
              <input name="address" value={form.address} onChange={handleChange} placeholder="주소를 입력하세요" />
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label>은행명</label>
              <input name="bankName" value={form.bankName} onChange={handleChange} placeholder="예: 국민은행" />
            </div>
            <div className="form-group">
              <label>계좌번호</label>
              <input name="bankAccount" value={form.bankAccount} onChange={handleChange} placeholder="계좌번호" inputMode="numeric" />
            </div>
          </div>
          <div className="form-group">
            <label>비고</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="참고사항을 입력하세요." />
          </div>
          <div className="admin-form-actions">
            <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
